// Apuração dos indicadores de metas físicas do PPA (I0153, I0154, I0155).
//
// Layout "C" (resumo em tiles + memória de cálculo + não computados), validado
// em protótipo antes da implementação.
//
// Somente leitura: consome GET /indicadores e não grava nada. Por isso não há
// obrigatoriedade nem maxLength nos campos — eles são filtro, não espelham
// coluna. A validação de período real mora no backend (normalizaPeriodo), e a
// tela mostra a mensagem dele em vez de reimplementar a regra.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
  MDBIcon,
  MDBSpinner,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
} from 'mdbreact';

import NavBar from '../../../components/NavBar';
import api from '../../../services/api';
import resolverMensagemErro from '../../../utils/resolverMensagemErro';
import { permissaoDeRota } from '../../../services/auth';

const TZ_ROTULO = 'America/Sao_Paulo';

// cores-sobrias.css é a fonte da verdade destes dois tons; ver o cabeçalho
// daquele arquivo antes de mexer (contraste, saturação e luminosidade juntos).
const COR_CONSTRUTIVA = '#1e8449';
const COR_DESTRUTIVA = '#d32f2f';
const COR_ACENTO = '#2bbbad'; // .btn-default do mdb.css
const COR_ATENCAO = '#f0ad4e';
const CINZA_FRACO = '#6c757d';

const MOTIVOS = {
  sem_conversao: 'Sem conversão cadastrada para a unidade',
  fator_invalido: 'Fator de conversão inválido',
};

const MESES_CURTOS = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

function hojeISO() {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

// Quadrimestre corrente — é o recorte que a SUPOF pede, então é o default útil.
function quadrimestreCorrente() {
  const d = new Date();
  const ano = d.getFullYear();
  const q = Math.floor(d.getMonth() / 4); // 0, 1 ou 2
  const inicioMes = q * 4 + 1;
  const fimMes = inicioMes + 3;
  const ultimoDia = new Date(ano, fimMes, 0).getDate();
  return {
    inicio: `${ano}-${String(inicioMes).padStart(2, '0')}-01`,
    fim: `${ano}-${String(fimMes).padStart(2, '0')}-${ultimoDia}`,
  };
}

function atalhosDoAno(ano) {
  return [
    { rotulo: `1º quadrimestre de ${ano}`, inicio: `${ano}-01-01`, fim: `${ano}-04-30` },
    { rotulo: `2º quadrimestre de ${ano}`, inicio: `${ano}-05-01`, fim: `${ano}-08-31` },
    { rotulo: `3º quadrimestre de ${ano}`, inicio: `${ano}-09-01`, fim: `${ano}-12-31` },
    { rotulo: `Exercício de ${ano} (ano inteiro)`, inicio: `${ano}-01-01`, fim: `${ano}-12-31` },
  ];
}

// Unidades que fracionam por natureza. Litros vem do backend sempre com
// toFixed(2); contagem de veículo é inteira e "946.441,00" sugeriria uma
// precisão que a contagem não tem.
const UNIDADES_FRACIONARIAS = ['litros'];

// As casas são decididas pela UNIDADE, não pelo valor.
//
// Antes o critério era `Number.isInteger(n)`, e isso fazia a mesma série
// alternar formato: um mês de litros que caísse exato em ,00 saía "12.410" e o
// seguinte "98.320,45". Num quadrimestre cujo total fechasse redondo, o ofício
// informaria "2.310.456 litros" enquanto o anterior informou "1.987.654,30" —
// na série do SIPLAG isso lê como mudança de critério de medição.
function nf(valor, unidade) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '-';
  const casas = UNIDADES_FRACIONARIAS.includes(unidade) ? 2 : 0;
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function dataBR(iso) {
  if (!iso) return '';
  const [ano, mes, dia] = String(iso).slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function carimboBR(iso) {
  if (!iso) return '';
  const data = dataBR(iso);
  const hora = String(iso).slice(11, 16);
  return `${data} às ${hora}`;
}

// Dá nome ao período quando ele é um recorte conhecido. Ver "01/05/2026 a
// 31/08/2026" exige traduzir mentalmente; ver "2º quadrimestre" não. Foi
// justamente esse reconhecimento que faltou quando o 1º quadrimestre foi
// escolhido e o 2º acabou congelado.
function nomeiaPeriodo(inicio, fim) {
  const ano = inicio.slice(0, 4);
  if (fim.slice(0, 4) !== ano) return null;
  const chave = `${inicio.slice(5)}|${fim.slice(5)}`;
  const nomes = {
    '01-01|04-30': '1º quadrimestre',
    '05-01|08-31': '2º quadrimestre',
    '09-01|12-31': '3º quadrimestre',
    '01-01|12-31': 'Exercício completo',
  };
  return nomes[chave] ? `${nomes[chave]} de ${ano}` : null;
}

function rotuloMes(chave) {
  const [, mes] = chave.split('-');
  return MESES_CURTOS[Number(mes) - 1] || chave;
}

export default function IndicadoresPPA() {
  const padrao = quadrimestreCorrente();
  const [inicio, setInicio] = useState(padrao.inicio);
  const [fim, setFim] = useState(padrao.fim);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [dados, setDados] = useState(null);
  // Acumulado do exercício até o fim do período — necessário para a barra de
  // progresso da meta anual, que o endpoint não devolve (ele responde só o
  // período pedido).
  const [acumulado, setAcumulado] = useState(null);
  // Distingue "não há acumulado a buscar" de "a busca do acumulado falhou".
  // Sem isso, a falha era invisível: a linha "Acumulado no exercício" sumia do
  // texto copiado e quem já tinha copiado antes não notava a ausência.
  const [acumuladoFalhou, setAcumuladoFalhou] = useState(false);
  const [modalCongelar, setModalCongelar] = useState(false);
  const [congelando, setCongelando] = useState(false);
  const [observacao, setObservacao] = useState('');

  // Conveniência de UI apenas: quem manda é o gate do backend
  // (gateCongelaIndicadores). Esconder o botão evita oferecer uma ação que
  // resultaria em 403, mas não é o controle de acesso.
  const podeCongelar = permissaoDeRota('/indicadores/congelar');

  // O filtro mudou e ninguém buscou: os números na tela são de OUTRO período.
  //
  // Isto não é firula. Congelar usa `dados.periodo` (o que foi apurado), que é
  // o certo — congelar um período que não foi calculado seria pior. Mas quem
  // trocou o filtro e clicou Congelar acha que está congelando o que escolheu.
  // Aconteceu em homologação: 1º quadrimestre selecionado, 2º congelado. Em
  // produção teria queimado o par 01/05→31/08 para sempre.
  //
  // Vale para todas as ações que levam número para fora — congelar, copiar
  // para o ofício e exportar CSV.
  const filtroDivergente =
    !!dados && (dados.periodo.inicio !== inicio || dados.periodo.fim !== fim);

  // Deriva de `dados`, NUNCA dos campos de filtro. `inicio`/`fim` mudam a cada
  // tecla; `dados` só muda ao buscar. Usar o filtro fazia o rótulo divergir do
  // número: alterar o campo Início para 2025 sem clicar Buscar e copiar o texto
  // produzia "Acumulado no exercício de 2025" com os valores de 2026 — direto
  // no documento do SEI.
  const anoApurado = dados
    ? Number(dados.periodo.inicio.slice(0, 4))
    : Number(inicio.slice(0, 4)) || new Date().getFullYear();

  // Os ATALHOS do select são outra coisa: ali o ano vem do filtro mesmo, porque
  // é o período que a pessoa está escolhendo — não o que ela já buscou. Manter
  // os dois separados é o ponto: rótulo de número segue `dados`, opção de filtro
  // segue o filtro.
  const anoDoFiltro = Number(inicio.slice(0, 4)) || new Date().getFullYear();

  const buscar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const { data } = await api.get('/indicadores', { params: { inicio, fim } });
      setDados(data);

      // Se o período já começa no início do exercício, ele É o acumulado —
      // não vale uma segunda ida ao banco.
      const inicioExercicio = `${inicio.slice(0, 4)}-01-01`;
      if (inicio === inicioExercicio) {
        setAcumulado(data);
        setAcumuladoFalhou(false);
      } else {
        try {
          const { data: acc } = await api.get('/indicadores', {
            params: { inicio: inicioExercicio, fim },
          });
          setAcumulado(acc);
          setAcumuladoFalhou(false);
        } catch {
          // Degrada sem quebrar — mas AVISANDO. Antes isto era silencioso, e a
          // consequência não era só visual: o texto copiado trocava a linha
          // "Acumulado no exercício" pela "Meta do exercício" sem nada indicar,
          // e o ofício seguia incompleto para a SUPOF.
          setAcumulado(null);
          setAcumuladoFalhou(true);
          toast.warn(
            'Não foi possível calcular o acumulado do exercício. ' +
              'O valor do período está correto, mas o percentual da meta não aparece.'
          );
        }
      }
    } catch (e) {
      // O interceptor global só emite toast em escrita; GET é por conta da tela.
      const mensagem =
        (e.response && e.response.data && e.response.data.erro) ||
        resolverMensagemErro(e);
      setErro(mensagem);
      setDados(null);
      setAcumulado(null);
      setAcumuladoFalhou(false);
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  }, [inicio, fim]);

  useEffect(() => {
    buscar();
    // Carrega uma vez, no quadrimestre corrente. As buscas seguintes são
    // explícitas — período é filtro, não deve refazer consulta a cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarAtalho = valor => {
    if (!valor) return;
    const [i, f] = valor.split('|');
    setInicio(i);
    setFim(f);
  };

  const acumuladoDe = codigo => {
    if (!acumulado) return null;
    const achado = (acumulado.indicadores || []).find(x => x.codigo === codigo);
    // Indicador indisponível no acumulado não tem valor: tratar como ausente,
    // senão o percentual sairia como NaN.
    if (!achado || achado.indisponivel) return null;
    return achado.valor;
  };

  // Mês parcial é o ÚLTIMO mês de `dados.periodo` quando ele não está completo.
  //
  // Duas correções em relação à versão anterior:
  // 1. Deriva de `dados`, não do filtro — senão trocar o período sem buscar
  //    colava "agosto é parcial" num quadrimestre que nem tem agosto, ou pior,
  //    fazia a ressalva sumir e agosto incompleto ia ao ofício como mês cheio.
  // 2. Considera parcial também o mês cortado por um `fim` no passado: buscar
  //    01/05–13/08 traz treze dias de agosto, e antes isso não era sinalizado
  //    porque a condição era "hoje está dentro do período".
  const hoje = hojeISO();
  const mesParcial = (() => {
    if (!dados) return null;
    const fimApurado = dados.periodo.fim;
    const [ano, mes] = fimApurado.split('-').map(Number);
    const ultimoDiaDoMes = new Date(ano, mes, 0).getDate();
    const mesFechou = Number(fimApurado.slice(8, 10)) >= ultimoDiaDoMes;
    // Mês só está completo se o período foi até o último dia dele E esse dia
    // já passou.
    if (mesFechou && fimApurado < hoje) return null;
    return fimApurado.slice(0, 7);
  })();

  const descartados = dados
    ? (dados.indicadores || []).flatMap(ind =>
        (ind.descartados || []).map(d => ({ ...d, indicador: ind.codigo }))
      )
    : [];

  // Não usar `indicadores[0]`: se justamente o primeiro estiver indisponível,
  // ele não tem `por_mes` e a tabela inteira sairia sem colunas.
  const mesesExibidos = dados
    ? Object.keys(
        ((dados.indicadores || []).find(i => i.por_mes) || {}).por_mes || {}
      )
    : [];

  // Texto plano de propósito: o que chega em caixa alta no SEI é conteúdo
  // ESTILIZADO colado do navegador, e o editor reaplica o estilo dele. Sem
  // marcação, o SEI respeita o texto.
  //
  // Acentuação normal: o destino é ofício. Os nomes dos indicadores já vêm
  // acentuados do backend, então tirar acento dos rótulos só produziria um
  // texto misturado — e não teria efeito nenhum sobre a caixa alta.
  const textoParaOficio = () => {
    if (!dados) return '';
    const linhas = [];
    linhas.push('Indicadores de metas físicas — PPA / Operação Foco');
    linhas.push(
      `Período: ${dataBR(dados.periodo.inicio)} a ${dataBR(dados.periodo.fim)}`
    );
    linhas.push(
      `Extraído em: ${carimboBR(dados.extraido_em)} (${dados.periodo.fuso})`
    );
    linhas.push('');
    (dados.indicadores || []).forEach(ind => {
      linhas.push(`${ind.codigo} — ${ind.nome}`);
      // Indicador que falhou não entra no ofício como número: sai declarado
      // como indisponível. Mandar 0 para a SUPOF seria pior que não mandar.
      if (ind.indisponivel) {
        linhas.push('Valor indisponível nesta extração — não utilizar.');
        linhas.push('');
        return;
      }
      linhas.push(`Realizado no período: ${nf(ind.valor, ind.unidade)} ${ind.unidade}`);
      const acc = acumuladoDe(ind.codigo);
      if (acc !== null) {
        const pct = (acc / ind.meta_exercicio) * 100;
        linhas.push(
          `Acumulado no exercício de ${anoApurado}: ${nf(acc, ind.unidade)} ${ind.unidade} ` +
            `(${pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% da meta de ${nf(ind.meta_exercicio, ind.unidade)})`
        );
      } else {
        linhas.push(`Meta do exercício de ${anoApurado}: ${nf(ind.meta_exercicio, ind.unidade)} ${ind.unidade}`);
        if (acumuladoFalhou) {
          linhas.push(
            'Acumulado do exercício não pôde ser calculado nesta extração.'
          );
        }
      }
      const meses = Object.entries(ind.por_mes || {})
        .map(([m, v]) => `${rotuloMes(m)}: ${nf(v, ind.unidade)}`)
        .join('; ');
      if (meses) linhas.push(`Abertura mensal: ${meses}`);
      if ((ind.descartados || []).length) {
        linhas.push(
          `Observação: ${ind.descartados.length} lançamento(s) do período não ` +
            'entraram no valor por problema na conversão de unidade.'
        );
      }
      linhas.push('');
    });
    if (mesParcial) {
      linhas.push(
        `Observação: ${rotuloMes(mesParcial)} é parcial — o período ainda não encerrou.`
      );
    }
    return linhas.join('\n');
  };

  const copiar = async () => {
    const texto = textoParaOficio();
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Texto copiado. Cole no documento do SEI.');
    } catch {
      // clipboard exige contexto seguro; sem ele, o fallback é o textarea.
      const area = document.createElement('textarea');
      area.value = texto;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(area);
      if (ok) toast.success('Texto copiado. Cole no documento do SEI.');
      else toast.error('Não foi possível copiar automaticamente.');
    }
  };

  const exportarCSV = () => {
    if (!dados) return;
    const sep = ';'; // Excel pt-BR
    const linhas = [
      ['Código', 'Indicador', 'Unidade', 'Período', 'Valor', 'Meta do exercício', 'Acumulado no exercício'],
    ];
    (dados.indicadores || []).forEach(ind => {
      const acc = acumuladoDe(ind.codigo);
      linhas.push([
        ind.codigo,
        ind.nome,
        ind.unidade,
        `${dataBR(dados.periodo.inicio)} a ${dataBR(dados.periodo.fim)}`,
        ind.indisponivel ? 'INDISPONÍVEL' : String(ind.valor).replace('.', ','),
        String(ind.meta_exercicio).replace('.', ','),
        acc === null
          ? (acumuladoFalhou ? 'NAO CALCULADO' : '')
          : String(acc).replace('.', ','),
      ]);
    });
    linhas.push([]);
    linhas.push(['Abertura mensal']);
    // Mês parcial precisa estar marcado AQUI também: o CSV vai para fora e,
    // sem a marca, um mês de 14 dias é indistinguível de mês fechado.
    linhas.push([
      'Código',
      ...mesesExibidos.map(m => (m === mesParcial ? `${m} (parcial)` : m)),
    ]);
    (dados.indicadores || []).forEach(ind => {
      linhas.push([
        ind.codigo,
        ...mesesExibidos.map(m =>
          ind.indisponivel
            ? 'INDISPONÍVEL'
            : String((ind.por_mes || {})[m] ?? 0).replace('.', ',')
        ),
      ]);
    });
    if (descartados.length) {
      linhas.push([]);
      linhas.push(['Não computados']);
      linhas.push(['Registro', 'Indicador', 'Item', 'Quantidade', 'Unidade', 'Posto', 'Data', 'Motivo']);
      descartados.forEach(d => {
        linhas.push([
          d.registro_id, d.indicador, d.item,
          String(d.quantidade).replace('.', ','),
          d.unidade, d.posto, dataBR(d.data),
          MOTIVOS[d.motivo] || d.motivo,
        ]);
      });
    }

    const csv = linhas
      .map(l => l.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(sep))
      .join('\r\n');
    // BOM para o Excel reconhecer UTF-8 e não comer os acentos.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `indicadores-ppa-${dados.periodo.inicio}-a-${dados.periodo.fim}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Congelar é ato, não consulta: fixa o número que vai a órgão externo e não
  // tem desfazer. `snapshot.js` recusa recongelar o mesmo par (inicio, fim) com
  // 409 e sem retificação — por isso a confirmação mostra as datas por extenso
  // e diz o que acontece.
  const congelar = async () => {
    setCongelando(true);
    try {
      const { data } = await api.post(
        '/indicadores/congelar',
        {
          inicio: dados.periodo.inicio,
          fim: dados.periodo.fim,
          observacao: observacao || undefined,
        },
        // Esta tela mostra a mensagem do backend por conta própria, incluindo o
        // 409 de período já congelado — que não é falha. Sem isto, o toast
        // genérico do interceptor sai junto, em vermelho, contradizendo o
        // específico.
        { toastNaTela: true }
      );
      setModalCongelar(false);
      setObservacao('');
      toast.success(
        `Período congelado: ${data.length} indicador(es) gravados em ` +
          `${dataBR(dados.periodo.inicio)} a ${dataBR(dados.periodo.fim)}.`
      );
    } catch (e) {
      const status = e.response && e.response.status;
      const mensagem =
        (e.response && e.response.data && e.response.data.erro) ||
        resolverMensagemErro(e);
      // 409 não é falha: é o sistema dizendo que o período já tem registro.
      if (status === 409) toast.info(mensagem);
      else toast.error(mensagem);
      setModalCongelar(false);
    } finally {
      setCongelando(false);
    }
  };

  const Barra = ({ ind }) => {
    const acc = acumuladoDe(ind.codigo);
    if (acc === null) return null;
    const antes = Math.max(0, acc - ind.valor);
    const pctAntes = Math.min(100, (antes / ind.meta_exercicio) * 100);
    const pctPeriodo = Math.max(
      0,
      Math.min(100 - pctAntes, (ind.valor / ind.meta_exercicio) * 100)
    );
    const pctTotal = (acc / ind.meta_exercicio) * 100;
    return (
      <div
        role="img"
        aria-label={`${pctTotal.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% da meta do exercício`}
        style={{ display: 'flex', height: '4px', background: '#eee', marginTop: '.5rem' }}
      >
        <span style={{ width: `${pctAntes}%`, background: '#a5d6b8' }} />
        <span style={{ width: `${pctPeriodo}%`, background: COR_CONSTRUTIVA }} />
      </div>
    );
  };

  const Marca = ({ ind }) => {
    const n = (ind.descartados || []).length;
    const estilo = {
      display: 'inline-block',
      padding: '.15rem .4rem',
      fontSize: '.65rem',
      fontWeight: 700,
      borderRadius: '.125rem',
      textTransform: 'uppercase',
      letterSpacing: '.02em',
    };
    if (!n) {
      return (
        <span style={{ ...estilo, background: '#e8f5ee', color: COR_CONSTRUTIVA, border: `1px solid ${COR_CONSTRUTIVA}` }}>
          completo
        </span>
      );
    }
    return (
      <span style={{ ...estilo, background: '#fff3cd', color: '#8a6d3b', border: `1px solid ${COR_ATENCAO}` }}>
        {n} não computado{n > 1 ? 's' : ''}
      </span>
    );
  };

  return (
    <>
      <NavBar />
      <MDBContainer className="mt-4" style={{ maxWidth: '1140px' }}>
        <h4 className="text-center font-weight-light mb-4">
          Apuração de Indicadores — PPA
        </h4>

        {/* FILTROS — período é filtro de consulta, não gravação: sem obrigatório. */}
        <MDBRow className="justify-content-center align-items-end mb-2">
          <MDBCol md="4" sm="6" className="mb-2">
            <label htmlFor="atalho" style={{ fontSize: '.75rem', color: CINZA_FRACO }}>
              Período
            </label>
            <select
              id="atalho"
              className="form-control"
              value={`${inicio}|${fim}`}
              onChange={e => aplicarAtalho(e.target.value)}
            >
              {!atalhosDoAno(anoDoFiltro).some(a => `${a.inicio}|${a.fim}` === `${inicio}|${fim}`) && (
                <option value={`${inicio}|${fim}`}>Personalizado</option>
              )}
              {atalhosDoAno(anoDoFiltro).map(a => (
                <option key={a.rotulo} value={`${a.inicio}|${a.fim}`}>
                  {a.rotulo}
                </option>
              ))}
            </select>
          </MDBCol>
          <MDBCol md="3" sm="6" className="mb-2">
            <label htmlFor="inicio" style={{ fontSize: '.75rem', color: CINZA_FRACO }}>
              Início
            </label>
            <input
              id="inicio"
              type="date"
              className="form-control"
              value={inicio}
              onChange={e => setInicio(e.target.value)}
            />
          </MDBCol>
          <MDBCol md="3" sm="6" className="mb-2">
            <label htmlFor="fim" style={{ fontSize: '.75rem', color: CINZA_FRACO }}>
              Fim
            </label>
            <input
              id="fim"
              type="date"
              className="form-control"
              value={fim}
              onChange={e => setFim(e.target.value)}
            />
          </MDBCol>
        </MDBRow>

        <div className="text-center mb-3">
          <MDBBtn size="sm" onClick={buscar} disabled={loading}>
            <MDBIcon icon="search" className="mr-1" /> Buscar
          </MDBBtn>
          <MDBBtn size="sm" onClick={copiar} disabled={!dados || loading || filtroDivergente}>
            <MDBIcon icon="copy" className="mr-1" /> Copiar para o ofício
          </MDBBtn>
          <MDBBtn size="sm" outline color="default" onClick={exportarCSV} disabled={!dados || loading || filtroDivergente}>
            <MDBIcon icon="download" className="mr-1" /> Exportar CSV
          </MDBBtn>
          {podeCongelar && (
            <MDBBtn
              size="sm"
              color="dark"
              onClick={() => setModalCongelar(true)}
              disabled={!dados || loading || filtroDivergente}
              title="Grava o resultado deste período como valor oficial prestado"
            >
              <MDBIcon icon="lock" className="mr-1" /> Congelar período
            </MDBBtn>
          )}
        </div>

        {dados && !loading && (
          <p className="text-center" style={{ fontSize: '.75rem', color: CINZA_FRACO }}>
            Extraído em {carimboBR(dados.extraido_em)} · fuso {dados.periodo.fuso || TZ_ROTULO}
            {mesParcial ? ` · ${rotuloMes(mesParcial)} parcial` : ''}
          </p>
        )}

        {filtroDivergente && !loading && (
          <p
            className="text-center"
            style={{ fontSize: '.85rem', color: COR_ATENCAO, fontWeight: 500 }}
          >
            <MDBIcon icon="exclamation-triangle" className="mr-1" />
            O período do filtro mudou. Os números abaixo ainda são de{' '}
            {dataBR(dados.periodo.inicio)} a {dataBR(dados.periodo.fim)} — clique em
            Buscar para atualizar.
          </p>
        )}

        {acumuladoFalhou && !loading && (
          <p
            className="text-center"
            style={{ fontSize: '.8rem', color: COR_ATENCAO, fontWeight: 500 }}
          >
            <MDBIcon icon="exclamation-triangle" className="mr-1" />
            Acumulado do exercício não pôde ser calculado — o valor do período
            está correto, mas o percentual da meta não aparece.
          </p>
        )}

        {loading && (
          <div className="text-center my-5">
            <MDBSpinner />
          </div>
        )}

        {erro && !loading && (
          <MDBCard className="mb-4">
            <MDBCardBody className="text-center">
              <MDBIcon icon="exclamation-triangle" size="2x" style={{ color: COR_DESTRUTIVA }} />
              <p className="mt-2 mb-0">{erro}</p>
            </MDBCardBody>
          </MDBCard>
        )}

        {/* RESUMO — um tile por indicador */}
        {dados && !loading && (
          <MDBRow>
            {(dados.indicadores || []).map(ind => {
              const acc = acumuladoDe(ind.codigo);
              return (
                <MDBCol md="4" sm="6" className="mb-3" key={ind.codigo}>
                  <div
                    style={{
                      background: '#fff',
                      boxShadow: '0 2px 5px 0 rgba(0,0,0,.16), 0 2px 10px 0 rgba(0,0,0,.12)',
                      padding: '1.1rem',
                      borderTop: `3px solid ${ind.indisponivel ? COR_DESTRUTIVA : COR_ACENTO}`,
                      height: '100%',
                    }}
                    title={ind.nome}
                  >
                    <div style={{ fontSize: '.75rem', color: ind.indisponivel ? COR_DESTRUTIVA : COR_ACENTO, fontWeight: 500, letterSpacing: '.04em' }}>
                      {ind.codigo}
                    </div>
                    {/* Um indicador pode falhar sozinho: o endpoint devolve os
                        outros normalmente e marca este com `indisponivel`.
                        Mostrar "0" aqui seria pior que mostrar o erro — zero é
                        um valor plausível e ninguém desconfiaria. */}
                    {ind.indisponivel ? (
                      <>
                        <div style={{ fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.2, marginTop: '.35rem', color: COR_DESTRUTIVA }}>
                          <MDBIcon icon="exclamation-triangle" className="mr-1" />
                          indisponível
                        </div>
                        <div style={{ fontSize: '.75rem', color: CINZA_FRACO, marginTop: '.4rem' }}>
                          {ind.erro}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: '1.75rem', fontWeight: 500, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
                          {nf(ind.valor, ind.unidade)}
                        </div>
                        <div style={{ fontSize: '.8rem', color: CINZA_FRACO }}>
                          {ind.unidade} no período
                        </div>
                        <Barra ind={ind} />
                        <div style={{ fontSize: '.75rem', color: CINZA_FRACO, marginTop: '.5rem' }}>
                          {acc !== null && (
                            <>
                              {((acc / ind.meta_exercicio) * 100).toLocaleString('pt-BR', {
                                maximumFractionDigits: 1,
                              })}
                              % da meta de {anoApurado} ·{' '}
                            </>
                          )}
                          <Marca ind={ind} />
                        </div>
                      </>
                    )}
                  </div>
                </MDBCol>
              );
            })}
          </MDBRow>
        )}

        {/* MEMÓRIA DE CÁLCULO */}
        {dados && !loading && !!mesesExibidos.length && (
          <>
            <h5 className="font-weight-light mt-4 mb-1">Memória de cálculo</h5>
            <p style={{ fontSize: '.85rem', color: CINZA_FRACO }}>
              Abertura mensal de cada indicador, para conferência e para o corpo do documento.
            </p>
            <MDBCard>
              <MDBCardBody className="p-0" style={{ overflowX: 'auto' }}>
                <MDBTable small className="mb-0">
                  <MDBTableHead>
                    <tr>
                      <th>Indicador</th>
                      {mesesExibidos.map(m => (
                        <th key={m} className="text-right">
                          {rotuloMes(m)}
                          {m === mesParcial ? ' *' : ''}
                        </th>
                      ))}
                      <th className="text-right">Total</th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {(dados.indicadores || []).map(ind => (
                      <tr key={ind.codigo}>
                        <td>
                          <strong>{ind.codigo}</strong>{' '}
                          <span style={{ color: CINZA_FRACO, fontSize: '.8rem' }}>{ind.unidade}</span>
                        </td>
                        {ind.indisponivel ? (
                          <td
                            colSpan={mesesExibidos.length + 1}
                            style={{ color: COR_DESTRUTIVA, fontSize: '.8rem' }}
                          >
                            indisponível — {ind.erro}
                          </td>
                        ) : (
                          <>
                            {mesesExibidos.map(m => (
                              <td key={m} className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {nf((ind.por_mes || {})[m] ?? 0, ind.unidade)}
                              </td>
                            ))}
                            <td className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              <strong>{nf(ind.valor, ind.unidade)}</strong>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </MDBTableBody>
                </MDBTable>
              </MDBCardBody>
            </MDBCard>
            {mesParcial && (
              <p style={{ fontSize: '.75rem', color: CINZA_FRACO, marginTop: '.5rem' }}>
                * {rotuloMes(mesParcial)} parcial — o período ainda não encerrou.
              </p>
            )}
          </>
        )}

        {/* NÃO COMPUTADOS */}
        {dados && !loading && (
          <>
            <h5 className="font-weight-light mt-4 mb-1">Não computados</h5>
            <p style={{ fontSize: '.85rem', color: CINZA_FRACO }}>
              Lançamentos do período que ficaram fora do valor apurado. O motivo de
              cada um está na última coluna — pode ser unidade sem conversão
              cadastrada para o item, ou fator de conversão inválido.
            </p>
            {!descartados.length ? (
              <MDBCard className="mb-5">
                <MDBCardBody style={{ fontSize: '.88rem', color: CINZA_FRACO }}>
                  Nenhum lançamento ficou de fora neste período.
                </MDBCardBody>
              </MDBCard>
            ) : (
              <MDBCard className="mb-5">
                <MDBCardBody className="p-0" style={{ overflowX: 'auto' }}>
                  <MDBTable small className="mb-0">
                    <MDBTableHead>
                      <tr>
                        <th className="text-right">Registro</th>
                        <th>Indicador</th>
                        <th>Item</th>
                        <th className="text-right">Quantidade</th>
                        <th>Posto</th>
                        <th>Data</th>
                        <th>Motivo</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {descartados.map(d => (
                        <tr key={`${d.indicador}-${d.registro_id}`}>
                          <td className="text-right">{d.registro_id}</td>
                          <td>{d.indicador}</td>
                          <td>{d.item}</td>
                          <td className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {nf(d.quantidade, 'litros')}{' '}
                            <strong style={{ color: COR_DESTRUTIVA }}>{d.unidade}</strong>
                          </td>
                          <td>{d.posto}</td>
                          <td>{dataBR(d.data)}</td>
                          <td>{MOTIVOS[d.motivo] || d.motivo}</td>
                        </tr>
                      ))}
                    </MDBTableBody>
                  </MDBTable>
                </MDBCardBody>
              </MDBCard>
            )}
          </>
        )}
        <MDBModal isOpen={modalCongelar} toggle={() => setModalCongelar(false)} size="lg">
          <MDBModalHeader toggle={() => setModalCongelar(false)}>
            <MDBIcon icon="lock" className="mr-2" style={{ color: CINZA_FRACO }} />
            Congelar período
          </MDBModalHeader>

          <MDBModalBody className="pt-3">
            {/* O PERÍODO é o elemento de destaque do modal, e de propósito: foi
                onde a tela enganou (1º quadrimestre escolhido, 2º congelado).
                Nome grande, datas embaixo, barra lateral para ancorar o olho. */}
            <div
              style={{
                borderLeft: `4px solid ${COR_ACENTO}`,
                background: '#f7f9fa',
                padding: '.85rem 1rem',
                marginBottom: '1.25rem',
              }}
            >
              <div
                style={{
                  fontSize: '.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '.08em',
                  color: CINZA_FRACO,
                }}
              >
                Período a congelar
              </div>
              {dados && nomeiaPeriodo(dados.periodo.inicio, dados.periodo.fim) && (
                <div style={{ fontSize: '1.35rem', fontWeight: 500, lineHeight: 1.25 }}>
                  {nomeiaPeriodo(dados.periodo.inicio, dados.periodo.fim)}
                </div>
              )}
              <div
                style={{
                  fontSize: '.95rem',
                  color: CINZA_FRACO,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {dados && `${dataBR(dados.periodo.inicio)} a ${dataBR(dados.periodo.fim)}`}
              </div>
            </div>

            {/* Valores: tabela, não lista. Números se comparam na vertical. */}
            <div
              style={{
                fontSize: '.68rem',
                textTransform: 'uppercase',
                letterSpacing: '.08em',
                color: CINZA_FRACO,
                marginBottom: '.35rem',
              }}
            >
              Valores que serão gravados
            </div>
            <table style={{ width: '100%', fontSize: '.9rem', marginBottom: '1.25rem' }}>
              <tbody>
                {(dados ? dados.indicadores : []).map(ind => (
                  <tr key={ind.codigo} style={{ borderTop: '1px solid #e9ecef' }}>
                    <td style={{ padding: '.5rem .5rem .5rem 0', whiteSpace: 'nowrap' }}>
                      <strong>{ind.codigo}</strong>
                    </td>
                    <td
                      style={{
                        padding: '.5rem',
                        color: CINZA_FRACO,
                        fontSize: '.82rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {ind.nome}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        padding: '.5rem 0 .5rem .5rem',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 500,
                      }}
                    >
                      {ind.indisponivel ? (
                        <span style={{ color: COR_DESTRUTIVA, fontWeight: 400 }}>
                          sem valor
                        </span>
                      ) : (
                        <>
                          {nf(ind.valor, ind.unidade)}{' '}
                          <span style={{ color: CINZA_FRACO, fontWeight: 400, fontSize: '.78rem' }}>
                            {ind.unidade}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Avisos condicionais: só aparecem quando há o que avisar. */}
            {mesParcial && (
              <div
                style={{
                  display: 'flex',
                  gap: '.6rem',
                  background: '#fff8e6',
                  borderLeft: `4px solid ${COR_ATENCAO}`,
                  padding: '.75rem .9rem',
                  marginBottom: '.75rem',
                  fontSize: '.85rem',
                  lineHeight: 1.45,
                }}
              >
                <MDBIcon icon="exclamation-triangle" style={{ color: '#8a6d3b', marginTop: '.15rem' }} />
                <div>
                  <strong>{rotuloMes(mesParcial)} ainda não terminou.</strong> Você vai gravar
                  um período incompleto — e estas datas não poderão ser congeladas de novo,
                  nem depois que o mês fechar. Para congelar o mês cheio mais tarde, use hoje
                  a data de ontem como fim.
                </div>
              </div>
            )}

            {(dados ? dados.indicadores : []).some(i => i.indisponivel) && (
              <div
                style={{
                  display: 'flex',
                  gap: '.6rem',
                  background: '#fdecea',
                  borderLeft: `4px solid ${COR_DESTRUTIVA}`,
                  padding: '.75rem .9rem',
                  marginBottom: '.75rem',
                  fontSize: '.85rem',
                  lineHeight: 1.45,
                }}
              >
                <MDBIcon icon="exclamation-triangle" style={{ color: COR_DESTRUTIVA, marginTop: '.15rem' }} />
                <div>
                  Um indicador não pôde ser apurado e será gravado{' '}
                  <strong>sem valor</strong>. Se precisar dele no congelado, resolva a falha
                  antes.
                </div>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: '.6rem',
                padding: '.75rem .9rem',
                marginBottom: '1.25rem',
                fontSize: '.85rem',
                lineHeight: 1.45,
                background: '#f1f3f5',
              }}
            >
              <MDBIcon icon="lock" style={{ color: CINZA_FRACO, marginTop: '.15rem' }} />
              <div>
                O congelado guarda estes valores com a data da extração e o seu nome, e
                <strong> não pode ser refeito</strong>: se o período for congelado de novo, o
                sistema recusa, para que o número já prestado não seja reescrito.
              </div>
            </div>

            <label
              htmlFor="obs"
              style={{
                fontSize: '.68rem',
                textTransform: 'uppercase',
                letterSpacing: '.08em',
                color: CINZA_FRACO,
              }}
            >
              Observação <span style={{ textTransform: 'none', letterSpacing: 0 }}>(opcional)</span>
            </label>
            <input
              id="obs"
              className="form-control"
              value={observacao}
              maxLength={500}
              onChange={e => setObservacao(e.target.value)}
              placeholder="Onde estes números foram informados, por exemplo"
            />
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn
              size="sm"
              flat
              onClick={() => setModalCongelar(false)}
              disabled={congelando}
            >
              Cancelar
            </MDBBtn>
            <MDBBtn size="sm" color="dark" onClick={congelar} disabled={congelando}>
              <MDBIcon icon="lock" className="mr-1" />
              {congelando ? 'Congelando…' : 'Congelar período'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>
      </MDBContainer>
    </>
  );
}
